import {ColumnsFlowComposer} from '@aptoma/pressflow/composition/ColumnsFlowComposer.ts';
import {createIdGenerator} from '@aptoma/pressflow/composition/FlowComposer.ts';
import type LayoutResult from '@aptoma/pressflow/composition/LayoutResult.ts';
import {ElementFactory} from '@aptoma/pressflow/elements/ElementFactory.ts';
import {ElementFlow} from '@aptoma/pressflow/flow/ElementFlow.ts';
import {default as FlowFrame} from '@aptoma/pressflow/flow/FlowFrame.ts';
import {rectToMultiShape} from '@aptoma/pressflow/geometry/index.ts';

import {div} from '../../../../src/utils/html.ts';
import {getFormatFromDimensions} from '../../../../src/utils/images.ts';
import type {ArticleCardLayoutRecord, CardLayout, NodeChunk} from '../../../../types/types.ts';
import {ReelView, type VideoPlayerOptions} from '../../../components/reel.ts';
import {createDivWithContent, createElement, parseHTMLElement, shouldNotBeOrphan} from '../../../lib/dom.ts';
import type {Store} from '../../../lib/store.ts';
import type {Article} from '../../../models/article.ts';
import {Reel} from '../../../models/reel.ts';
import {applySafeAreaStyles, getBestTextPlacement, getImageSafeAreas} from './image-safe-area-utils.ts';
import {isIPadOrIPhone} from './scroll-fix.ts';
import './../_pressflow.css';
import type {ReaderItem} from '../../../models/reader-edition.ts';
import {type EdgeSafeAreas, visualizeSafeAreas} from './ImageSafeAreaAnalyzer.ts';
import {
	shallowCloneChunk,
	splitBodyTextAndFigures,
	splitDesktopChunks,
	splitIntoChunks,
	splitMobileChunks
} from './markup.ts';

export const EVENT_CARD_ACTIVATED = 'card:activated';

export interface CardComponent {
	el: HTMLElement;

	render(options: VideoPlayerOptions): void;

	registerEventListeners(): void;

	unregisterEventListeners(): void;

	activate(mobileMode: boolean): void;

	deactivate(mobileMode: boolean): void;
}

const CHARS_PER_COLUMN = 1200;

export class Cards {
	public item: Article | Reel;
	public currentCardNumber = 0;
	public dialogEl: HTMLDialogElement | undefined;
	public cardCount = 0;
	public currentCardEl: HTMLElement | undefined;

	private cardEls: HTMLElement[] = [];
	private cardComponents: (CardComponent | null)[] = [];
	private clonedCurrentCardEl: HTMLElement | undefined;
	private imageEventListener: EventListener | undefined;
	private el: HTMLElement;
	private articleEl: HTMLElement;
	private mobileMode: MediaQueryList;
	private store: Store;
	private chunks: NodeChunk[] = [];
	private cardIntersectionObserver: IntersectionObserver | null = null;
	private isScrollingProgrammatically = false;
	private hasPostProcessed = false;
	private targetCardNumberAfterPostProcess: number | null = null;
	private scrollEndTimeout: number | null = null;
	private debugMode = false;

	constructor(
		article: Article | Reel,
		el: HTMLElement,
		articleEl: HTMLElement,
		mobileMode: MediaQueryList,
		store: Store
	) {
		this.item = article;
		this.el = el;
		this.articleEl = articleEl;
		this.mobileMode = mobileMode;
		this.store = store;
	}

	private setupCardIntersectionObserver() {
		// Only use intersection observer in mobile mode where scroll snap is active
		if (!this.mobileMode.matches) {
			return;
		}

		if (this.cardIntersectionObserver) {
			this.cardIntersectionObserver.disconnect();
		}

		// This should trigger when a card is manually scrolled into view.
		// It should ensure that the card swiped into view is correctly activated.
		// It should not trigger when the card scrolling into view is triggered from other means (e.g., programmatic scroll from tap/keyboard/menu).
		this.cardIntersectionObserver = new IntersectionObserver(
			(entries: IntersectionObserverEntry[]) => {
				// Find the card that's most visible
				let mostVisibleEntry: IntersectionObserverEntry | null = null;
				let maxRatio = 0;

				for (const entry of entries) {
					if (entry.intersectionRatio > maxRatio) {
						maxRatio = entry.intersectionRatio;
						mostVisibleEntry = entry;
					}
				}

				// Only activate if we have a visible card and we're not programmatically scrolling
				if (mostVisibleEntry && mostVisibleEntry.intersectionRatio >= 0.75) {
					const cardEl = mostVisibleEntry.target as HTMLElement;
					const cardNumber = Number(cardEl.dataset.cardNumber);

					// Only activate if it's different from the current card and not programmatically scrolling
					if (cardNumber !== this.currentCardNumber && !this.isScrollingProgrammatically) {
						// Mark as 'manual' source so the event is dispatched for URL update
						this.activateCard(cardNumber, true, 'manual');
					}
				}
			},
			{
				root: this.el, // The cards container
				rootMargin: '0px',
				threshold: [0, 0.25, 0.5, 0.75, 1.0] // Multiple thresholds for better detection
			}
		);
	}

	connectIntersectionObserver() {
		if (!this.mobileMode.matches) {
			return;
		}

		this.setupCardIntersectionObserver();
		this.cardEls.forEach((cardEl: HTMLElement) => {
			this.cardIntersectionObserver?.observe(cardEl);
		});
		this.setupVideoScrollCorrection();
	}

	disconnectIntersectionObserver() {
		if (!this.mobileMode.matches) {
			return;
		}

		if (this.cardIntersectionObserver) {
			this.cardIntersectionObserver.disconnect();
			this.cardIntersectionObserver = null;
		}
		this.teardownVideoScrollCorrection();
	}

	private setupVideoScrollCorrection() {
		if (!this.mobileMode.matches || !isIPadOrIPhone()) {
			return;
		}

		// Check if any cards contain video elements
		const hasVideoCards = this.cardEls.some((card) => card.querySelector('video') !== null);
		if (!hasVideoCards) {
			return;
		}

		// Listen for scroll events on the cards container
		this.el.addEventListener('scroll', this.handleVideoScroll, {passive: true});
	}

	private teardownVideoScrollCorrection() {
		this.el.removeEventListener('scroll', this.handleVideoScroll);
		if (this.scrollEndTimeout) {
			clearTimeout(this.scrollEndTimeout);
			this.scrollEndTimeout = null;
		}
	}

	private handleVideoScroll = () => {
		// Clear existing timeout
		if (this.scrollEndTimeout) {
			clearTimeout(this.scrollEndTimeout);
		}

		// Wait for scroll to stop
		this.scrollEndTimeout = window.setTimeout(() => {
			this.correctVideoCardPosition();
		}, 150);
	};

	private correctVideoCardPosition() {
		if (this.isScrollingProgrammatically) {
			return;
		}

		// Find the card closest to the viewport
		let closestCard: HTMLElement | null = null;
		let minDistance = Number.POSITIVE_INFINITY;

		for (const cardEl of this.cardEls) {
			const cardLeft = cardEl.offsetLeft;
			const scrollLeft = this.el.scrollLeft;
			const distance = Math.abs(scrollLeft - cardLeft);

			if (distance < minDistance) {
				minDistance = distance;
				closestCard = cardEl;
			}
		}

		if (!closestCard) {
			return;
		}

		// Check if the closest card or adjacent cards have video
		const cardNumber = Number(closestCard.dataset.cardNumber);
		if (cardNumber > 2) {
			// Only care about front page video for now
			return;
		}
		const hasVideo = closestCard.querySelector('video') !== null;
		const prevCard = this.cardEls[cardNumber - 2];
		const nextCard = this.cardEls[cardNumber];
		const adjacentHasVideo =
			(prevCard && prevCard.querySelector('video') !== null) ||
			(nextCard && nextCard.querySelector('video') !== null);

		// Only snap if we're near a video card and not already perfectly aligned
		if ((hasVideo || adjacentHasVideo) && minDistance > 5) {
			// this.isScrollingProgrammatically = true;
			this.el.scrollTo({
				left: closestCard.offsetLeft,
				behavior: 'smooth'
			});

			setTimeout(() => {
				this.isScrollingProgrammatically = false;
			}, 300);
		}
	}

	private unobserveCard(cardEl: HTMLElement) {
		this.cardIntersectionObserver?.unobserve(cardEl);
	}

	cloneCardByNumber(cardNumber: number) {
		if (Number(this.clonedCurrentCardEl?.dataset.cardNumber) === cardNumber) {
			return;
		}

		const cardEl = this.cardEls[cardNumber - 1];

		if (this.clonedCurrentCardEl) {
			this.clonedCurrentCardEl.remove();
		}

		this.clonedCurrentCardEl = cardEl.cloneNode(true) as HTMLElement;
		cardEl.insertAdjacentElement('afterend', this.clonedCurrentCardEl);
		this.clonedCurrentCardEl.style.setProperty('--card-offset', '0');
		this.clonedCurrentCardEl.dataset.cardState = 'future';
		this.clonedCurrentCardEl.classList.add('card--cloned-preview');
	}

	registerCardEventListeners(cardEl: HTMLElement, cardNumber: number) {
		if (!this.imageEventListener) {
			this.imageEventListener = (event: Event) => {
				const pointerEvent = event as PointerEvent;
				if (pointerEvent.buttons !== 1) {
					return;
				}

				const targetEl = pointerEvent.currentTarget as HTMLElement;
				const clickedCard = targetEl.closest('.card') as HTMLElement;
				if (clickedCard.dataset.cardState !== 'current') {
					return;
				}

				// Image zoom modal is only relevant on desktop
				if (this.mobileMode.matches || pointerEvent.pointerType !== 'mouse') {
					return;
				}

				pointerEvent.stopImmediatePropagation();
				pointerEvent.preventDefault();

				this.dialogEl = createElement(cardEl.ownerDocument, 'dialog') as HTMLDialogElement;
				this.dialogEl.innerHTML = targetEl.outerHTML;
				const imgEl = this.dialogEl.querySelector('img');
				if (imgEl?.dataset.srcFull) {
					imgEl.src = imgEl.dataset.srcFull;
				}

				this.dialogEl.classList.add('image-modal');
				this.el.appendChild(this.dialogEl);
				this.dialogEl.showModal();
				this.dialogEl.addEventListener('pointerdown', (event) => {
					if (event.buttons > 1) {
						return;
					}
					this.dialogEl?.close();
					this.dialogEl?.remove();
				});
			};
		}

		cardEl.querySelectorAll('.figure, .byline-photo img').forEach((el) => {
			if (this.imageEventListener) {
				el.addEventListener('pointerdown', this.imageEventListener);
			}
		});

		this.getCardComponent(cardNumber)?.registerEventListeners();
	}

	unregisterCardEventListeners(cardEl: HTMLElement, cardNumber: number) {
		cardEl.querySelectorAll('.figure, .byline-photo img').forEach((el) => {
			if (this.imageEventListener) {
				el.removeEventListener('pointerdown', this.imageEventListener);
			}
		});

		this.getCardComponent(cardNumber)?.unregisterEventListeners();
	}

	private getCardComponent(cardNumber: number) {
		return this.cardComponents[cardNumber - 1];
	}

	deactivateCurrentCard() {
		if (this.cardEls[this.currentCardNumber - 1]) {
			this.deactivateCard(this.cardEls[this.currentCardNumber - 1]);
		}
	}

	deactivateCard(cardEl: HTMLElement) {
		const cardNumber = Number(cardEl.dataset.cardNumber);
		this.unregisterCardEventListeners(cardEl, cardNumber);
		this.getCardComponent(cardNumber)?.deactivate(this.mobileMode.matches);
	}

	activateCard(
		targetCardNumber: number,
		skipScroll = true,
		source: 'programmatic' | 'manual' | 'postprocess' = 'programmatic'
	) {
		this.removeActiveDialog();
		if (!this.cardEls[targetCardNumber - 1]) {
			if (!this.hasPostProcessed) {
				this.targetCardNumberAfterPostProcess = targetCardNumber;
			}
			return;
		}

		// Deactivate current card if valid
		if (this.currentCardNumber > 0 && this.cardEls[this.currentCardNumber - 1]) {
			this.deactivateCard(this.cardEls[this.currentCardNumber - 1]);
		}

		this.currentCardNumber = targetCardNumber;

		for (const cardEl of this.cardEls) {
			this.updateCardState(cardEl);

			const cardNumber = Number(cardEl.dataset.cardNumber);
			if (cardNumber === targetCardNumber) {
				this.registerCardEventListeners(cardEl, targetCardNumber);
				this.getCardComponent(targetCardNumber)?.activate(this.mobileMode.matches);
			}
		}

		this.updateAllProgressIndicators();
		this.currentCardEl = this.cardEls[targetCardNumber - 1];

		if (!skipScroll && this.mobileMode.matches) {
			this.scrollToCard(this.currentCardEl);
		}

		// Only dispatch event for manual scroll activations
		// Programmatic activations should handle URL updates at the call site
		if (source === 'manual') {
			this.articleEl.dispatchEvent(
				new CustomEvent(EVENT_CARD_ACTIVATED, {
					bubbles: true,
					detail: {
						cardNumber: targetCardNumber,
						totalCards: this.cardCount,
						articleId: this.item.id
					}
				})
			);
		}
	}

	private scrollToCard(cardEl: HTMLElement) {
		const supportsScrollEnd = 'onscrollend' in cardEl;
		const scrollDuration = 150;

		const onScrollEnd = () => {
			this.isScrollingProgrammatically = false;
		};

		const distance = smoothScrollTo(cardEl, this.el, scrollDuration);
		if (distance !== 0) {
			// Set flag to prevent intersection observer from triggering during programmatic scroll
			this.isScrollingProgrammatically = true;
			if (supportsScrollEnd) {
				cardEl.parentElement?.addEventListener('scrollend', onScrollEnd.bind(this), {once: true});
			} else {
				setTimeout(onScrollEnd.bind(this), scrollDuration);
			}
		}
	}

	resetCards() {
		this.el.innerHTML = '';
		this.cardEls = [];
		this.cardComponents = [];
		this.cardCount = 0;
		this.currentCardNumber = 1;
		this.currentCardEl = undefined;
		const progressIndicators = this.articleEl.querySelector('.article-progress');
		if (progressIndicators) {
			progressIndicators.innerHTML = '';
		}
	}

	renderCards(layouts: Record<number, CardLayout | undefined>) {
		const mobileMode = this.mobileMode.matches;

		this.resetCards();

		if (document.querySelector('[data-mode="pressflow"]')) {
			return;
		}

		// console.log(this.article.contentGroups);
		if (this.item instanceof Reel) {
			return this.renderCardsFromReel(mobileMode, layouts);
		}

		if (this.item.contentGroups) {
			return this.renderCardsFromContentGroups(this.item, mobileMode, layouts);
		}

		const [bodyTextNodes, figures] = splitBodyTextAndFigures(
			this.item.markup,
			this.el.ownerDocument,
			this.item.isArticleGroup
		);
		// Mobile sizes are based on specific viewport size, adjust expected content fit based on additional available space
		const extraHeight = (this.el.offsetHeight / 618) * (375 / this.el.offsetWidth);
		this.chunks = splitIntoChunks(this.item, bodyTextNodes, figures, mobileMode, layouts, extraHeight);

		const [firstCardChunks, ...otherCardChunks] = this.chunks;

		this.renderCoverCard(this.item, firstCardChunks);
		// this.cloneCardByNumber(1);

		otherCardChunks.forEach((chunk, index) => {
			this.renderCard(chunk, index + 1); // +1 since the first card is rendered already
		});
	}

	renderWithPressFlow(item: ReaderItem, mobileMode: boolean, layouts: Record<number, CardLayout | undefined>) {
		const extraMobileHeight = (this.el.offsetHeight / 618) * (375 / this.el.offsetWidth);
		const chunks = getChunks(this.el);
		let article: Article | undefined;
		let firstArticleCardIndex = 0;

		if (item instanceof Reel) {
			const [coverCardEl, reelView] = this.renderReelCoverCard(item);
			this.addCard(coverCardEl, reelView);
			if (!item.relatedArticle) {
				return chunks;
			}
			article = item.relatedArticle;
			firstArticleCardIndex = 1;
		} else {
			article = item;
		}

		if (mobileMode) {
			const overflowElements = this.renderPressflowMobileCoverCard(article, chunks[0], firstArticleCardIndex);
			Array.from({length: chunks.length - 1}).map((_, index) => {
				const chunk = chunks[index + 1];
				const cardEl = this.createPressFlowCard(index + firstArticleCardIndex + 1, chunk, overflowElements);
				this.addCard(cardEl, null);
				return cardEl;
			});

			return chunks;
		}

		// Desktop mode
		this.renderPressflowCoverCard(article, chunks[0], firstArticleCardIndex);
		Array.from({length: chunks.length - 1}).map((_, index) => {
			const chunk = chunks[index + 1];
			const cardEl = this.createPressFlowCard(this.cardCount, chunk);
			if (chunk?.figures) {
				cardEl.classList.add('card--figure');
				cardEl.dataset.figureCount = `${chunk.figures.length}`;
				cardEl.prepend(...chunk.figures);
			}
			this.addCard(cardEl, null);

			return cardEl;
		});
		return chunks;

		function getChunks(el: HTMLElement) {
			if (item instanceof Reel) {
				if (item.relatedArticle?.contentGroups) {
					return splitContentGroups(
						item.relatedArticle.contentGroups.map(shallowCloneChunk),
						item.relatedArticle
					);
				}
				return [];
			}

			if (!item.contentGroups) {
				const [bodyTextNodes, figures] = splitBodyTextAndFigures(
					item.markup,
					el.ownerDocument,
					item.isArticleGroup
				);
				// Mobile sizes are based on specific viewport size, adjust expected content fit based on additional available space
				const extraHeight = (el.offsetHeight / 618) * (375 / el.offsetWidth);
				return splitIntoChunks(item, bodyTextNodes, figures, mobileMode, layouts, extraHeight);
			}

			return splitContentGroups(item.contentGroups.map(shallowCloneChunk), item);

			function splitContentGroups(contentGroups: NodeChunk[], item: Article) {
				if (mobileMode) {
					return splitMobileChunks(contentGroups, item, extraMobileHeight, layouts);
				}

				return splitDesktopChunks(contentGroups, item, layouts);
			}
		}
	}

	postProcessCardsWithPressflow(layouts: Record<number, CardLayout | undefined>, forceRedraw = false) {
		if (this.hasPostProcessed && !forceRedraw) {
			this.connectIntersectionObserver();
			return;
		}

		this.beforePostProcess();
		this.resetCards();
		const chunks = this.renderWithPressFlow(this.item as Article, this.mobileMode.matches, layouts);

		const htmlElements: HTMLElement[] = [];
		chunks.forEach((chunk) => {
			// htmlElements.push(...chunk.figures);
			htmlElements.push(...chunk.nodes);
			if (chunk.extraNodes) {
				htmlElements.push(...chunk.extraNodes.flat());
			}
		});

		const elementFactory = new ElementFactory();
		elementFactory.registerDefaults();
		const filteredHtmlElements = htmlElements.filter((el) => isSupportedElement(el));

		const elementCollection = elementFactory.createFromList(filteredHtmlElements);

		let frames = this.cardEls.flatMap(collectFrames);
		if (!frames.length) {
			const cardEl = this.createPressFlowCard(this.cardCount);
			this.addCard(cardEl, null);
			frames = this.cardEls.flatMap(collectFrames);

			if (!frames.length) {
				console.warn(`No frames found for ${this.item.id}`);
				this.afterPostProcess();
				return;
			}
		}

		const elementFlow = new ElementFlow(`article-${this.item.id}`, frames, elementCollection);

		const composer = new ColumnsFlowComposer({
			elementFlow,
			context: {
				nodeDataWhiteLists: {attributes: new Map(), classNames: new Map()},
				idGenerator: createIdGenerator(`a-${this.item.id}`)
			}
		});
		let recalculateStyles = true;
		let layoutResult: LayoutResult;
		try {
			layoutResult = composer.compose(recalculateStyles);
		} catch (e) {
			console.log(e);
			this.afterPostProcess();
			return;
		}

		recalculateStyles = false;

		let guard = 5;
		while (layoutResult.hasOverflow() && guard--) {
			const cardEl = this.createPressFlowCard(this.cardCount);
			this.addCard(cardEl, null);
			collectFrames(cardEl).forEach((frame) => {
				elementFlow.addFrame(frame);
			});
			elementFlow.frames.forEach((frame) => {
				frame.el.innerHTML = '';
			});
			layoutResult = composer.compose(recalculateStyles);
		}

		layoutResult.render();
		this.cleanUpCards();
		this.cleanUpEmptyCards();

		this.afterPostProcess();

		function isSupportedElement(el: HTMLElement) {
			const blockedTags = ['aside'];

			if (blockedTags.includes(el.localName)) {
				console.log('Skipped element', el);
				return false;
			}

			return true;
		}

		function collectFrames(el: HTMLElement) {
			const figureEl = el.querySelector('.figure') as HTMLElement;
			let exclusionArea = null;
			// TODO: Only do this on cover cards?
			if (figureEl) {
				exclusionArea = rectToMultiShape(getExpandedBoundingRect(figureEl));
			}
			return Array.from(el.querySelectorAll<HTMLDivElement>('.article-body'))
				.map((articleBodyPart) => {
					articleBodyPart.innerHTML = '';
					const styles = getComputedStyle(articleBodyPart);
					const baseline = Number.parseFloat(styles.lineHeight);
					const rect = articleBodyPart.getBoundingClientRect();
					if (rect.height < baseline) {
						return null;
					}

					return new FlowFrame({
						el: articleBodyPart,
						rect,
						exclusionArea
					});
				})
				.filter((f) => f instanceof FlowFrame);
		}
	}

	// Apply safe area positioning for header-on-image layout
	applyHeaderOnImageSafeArea(cardEl: HTMLElement) {
		const imgEl = cardEl.querySelector('.figure img') as HTMLImageElement;
		if (!imgEl) {
			// Fallback: position at bottom-left if no image
			cardEl.classList.add('card--safe-area-bottom-left');
			return;
		}

		const areas = getImageSafeAreas(imgEl);
		let bestArea = getBestTextPlacement(areas);

		// Fallback to corner areas if getBestTextPlacement returns nothing
		if (!bestArea) {
			bestArea = areas.bottomLeft || areas.bottomRight || areas.topLeft || areas.topRight;
		}

		// Default to bottom-left if still no area found
		if (!bestArea) {
			cardEl.classList.add('card--safe-area-bottom-left');
			return;
		}

		applySafeAreaStyles(cardEl, bestArea);

		if (this.debugMode) {
			this.visualizeSafeImageAreas(imgEl, areas);
		}
	}

	analyzeCoverImage(cardEl: HTMLElement) {
		const imgEl = cardEl.querySelector('.figure img') as HTMLImageElement;
		if (!imgEl) return;

		const areas = getImageSafeAreas(imgEl);
		const bestArea = getBestTextPlacement(areas);
		if (bestArea) {
			applySafeAreaStyles(cardEl, bestArea);
		}

		if (this.debugMode) {
			this.visualizeSafeImageAreas(imgEl, areas);
		}
	}

	private visualizeSafeImageAreas(imgEl: HTMLImageElement, areas: EdgeSafeAreas) {
		const canvasEl = visualizeSafeAreas(imgEl, areas);
		canvasEl.style.position = 'absolute';
		canvasEl.style.width = '100%';
		canvasEl.style.height = '100%';
		imgEl.parentElement?.append(canvasEl);
	}

	createPressFlowCard(index: number, chunk: NodeChunk | null = null, overflowElements: HTMLElement[] = []) {
		const mobileMode = this.mobileMode.matches;
		const cardEl = this.createCard(index, chunk, null, undefined, ['card--pf']);
		cardEl.innerHTML = mobileMode
			? `<div class="article-body article-body--part-1 article-body--pressflow"></div>`
			: `
<div class="article-body article-body--part-1 article-body--pressflow"></div>
<div class="article-body article-body--part-2 article-body--pressflow"></div>
<div class="article-body article-body--part-3 article-body--pressflow"></div>
`;
		cardEl.prepend(...overflowElements);
		overflowElements.length = 0;

		if (chunk?.figures) {
			cardEl.classList.add('card--figure');
			cardEl.dataset.figureCount = `${chunk.figures.length}`;
			cardEl.prepend(...chunk.figures);
		}

		return cardEl;
	}

	renderCardsFromReel(mobileMode: boolean, layouts: Record<number, CardLayout | undefined>) {
		const reel = this.item as Reel;
		const [coverCardEl, reelView] = this.renderReelCoverCard(reel);
		this.addCard(coverCardEl, reelView);
		if (reel.relatedArticle) {
			this.renderCardsFromContentGroups(reel.relatedArticle, mobileMode, layouts, 1);
		}
	}

	renderReelCoverCard(reel: Reel): [HTMLElement, ReelView] {
		const coverCardEl = this.createCard(0, null, null, undefined);
		coverCardEl.classList.add('card--reel');
		coverCardEl.innerHTML = '';
		const reelView = new ReelView(reel, this.store);
		reelView.render();
		coverCardEl.append(reelView.el);

		return [coverCardEl, reelView];
	}

	renderCardsFromContentGroups(
		article: Article,
		mobileMode: boolean,
		layouts: Record<number, CardLayout | undefined>,
		firstCardIndex = 0
	) {
		if (!article.contentGroups?.length) {
			return;
		}

		const extraMobileHeight = (this.el.offsetHeight / 618) * (375 / this.el.offsetWidth);

		const contentGroups = article.contentGroups.map(shallowCloneChunk);
		this.chunks = mobileMode
			? splitMobileChunks(contentGroups, article, extraMobileHeight, layouts)
			: splitDesktopChunks(contentGroups, article, layouts);

		const firstCardChunk = this.chunks[0];
		firstCardChunk.layout = layouts[1];

		this.renderCoverCard(article, firstCardChunk, firstCardIndex);
		// this.cloneCardByNumber(1);

		this.chunks.slice(1).forEach((chunk, index) => {
			this.renderCard(chunk, index + firstCardIndex + 1); // +1 to account for first card
		});
	}

	renderCoverCard(article: Article, chunk: NodeChunk, firstCardIndex = 0) {
		const markup = `
	${div(article.pretitle, 'pretitle')}
	${div(article.title, 'title')}
	${div(article.intro, 'intro')}
	${article.byline?.html || ''}
	${div(article.story, 'story')}
	${chunk.figures[0]?.outerHTML || ''}
	${div(chunk.nodes.map((el: HTMLElement) => el.outerHTML).join('\n'), 'article-body')}
			`;

		if (!this.mobileMode.matches && chunk.nodes.length === 0 && !chunk.layout) {
			chunk.layout = '1col-text-2col-image';
		}
		this.addCard(this.createCard(firstCardIndex, chunk, markup, chunk.layout, ['card--cover']), null);
	}

	renderPressflowCoverCard(article: Article, chunk: NodeChunk, firstCardIndex = 0) {
		const hasAsset = Boolean(chunk.figures[0]?.outerHTML);
		const titleClasses = ['title'];
		if (article.title.length >= 100) {
			titleClasses.push('title--long', `title--${article.title.length}-chars`);
		}

		let layout: CardLayout | undefined = 'pressflow-cover-no-image';
		let isHeaderOnImage = false;
		if (hasAsset) {
			// Uncomment this if there's a rendering issue with another layout
			// chunk.layout = 'pressflow-cover';
			layout = chunk.layout || 'pressflow-cover';
			isHeaderOnImage = [
				'pressflow-cover-header-on-image',
				'pressflow-header-overlay-left',
				'pressflow-header-overlay-top',
				'pressflow-header-overlay-right',
				'pressflow-reverse-L-image-overlay'
			].includes(layout);
		}

		const headerContent = `
	<div class="header-content">
		${div(article.pretitle, 'pretitle')}
		${div(article.title, titleClasses.join(' '))}
		${div(article.intro, 'intro')}
		${article.byline?.html || ''}
		${div(article.story, 'story')}
	</div>
`;

		const markup =
			layout === 'pressflow-cover-header-on-image'
				? `
	${chunk.figures[0]?.outerHTML || ''}
	${headerContent}
`
				: `
	${div(article.pretitle, 'pretitle')}
	${div(article.title, titleClasses.join(' '))}
	${div(article.intro, 'intro')}
	${article.byline?.html || ''}
	${div(article.story, 'story')}
	${chunk.figures[0]?.outerHTML || ''}
	<div class="article-body article-body--part-1 article-body--pressflow"></div>
	<div class="article-body article-body--part-2 article-body--pressflow"></div>
	<div class="article-body article-body--part-3 article-body--pressflow"></div>
`;

		const cardEl = this.createCard(firstCardIndex, chunk, markup, layout, ['card--cover']);
		if (this.debugMode) {
			cardEl.classList.add('card--debug');
		}
		this.addCard(cardEl, null);

		if (isHeaderOnImage) {
			this.applyHeaderOnImageSafeArea(cardEl);
		}
	}

	renderPressflowMobileCoverCard(article: Article, chunk: NodeChunk, firstCardIndex = 0): HTMLElement[] {
		const titleClasses = ['title', `title--${article.title.length}-chars`];
		if (article.title.length >= 70) {
			titleClasses.push('title--long');
		}
		const cardClasses = ['card--cover'];

		const hasAsset = Boolean(chunk.figures[0]?.outerHTML);
		const bylineMarkup = article.byline?.html || '';
		const assetMarkup = chunk.figures[0]?.outerHTML || '';

		const layout = !hasAsset ? 'pressflow-cover' : chunk.layout || 'pressflow-cover';

		const pretitleEl = createDivWithContent(this.el.ownerDocument, 'pretitle', article.pretitle);
		const titleEl = createDivWithContent(this.el.ownerDocument, titleClasses, article.title);
		const introEl = createDivWithContent(this.el.ownerDocument, 'intro', article.intro);
		const bylineEl = parseHTMLElement(this.el.ownerDocument, bylineMarkup);
		const storyEl = createDivWithContent(this.el.ownerDocument, 'story', article.story);
		const assetEl = parseHTMLElement(this.el.ownerDocument, assetMarkup);
		const bodyEl = createElement(this.el.ownerDocument, 'div', [
			'article-body',
			'article-body--part-1',
			'article-body--pressflow'
		]);
		const headerContentEl = createElement(this.el.ownerDocument, 'div', 'header-content');

		const cardEl = this.createCard(firstCardIndex, chunk, '', layout, cardClasses);
		this.addCard(cardEl, null);
		addAll(cardEl, [pretitleEl, titleEl, introEl, bylineEl, storyEl, assetEl, bodyEl]);

		switch (layout) {
			case 'pressflow-cover':
				return applyDefaultLayout();
			case 'pressflow-mobile-header-square':
				return applySquareLayout();
			case 'pressflow-mobile-header-portrait':
				return applyPortraitLayout();
			case 'pressflow-mobile-full':
				return applyFullHeightImageLayout();
			default:
				return [];
		}

		function applyDefaultLayout(): HTMLElement[] {
			return detectAndHandleDynamicOverflow(true);
		}

		function applySquareLayout(): HTMLElement[] {
			addAll(headerContentEl, [pretitleEl, titleEl]);
			cardEl.prepend(headerContentEl);
			return detectAndHandleDynamicOverflow(false);
		}

		function applyPortraitLayout(): HTMLElement[] {
			addAll(headerContentEl, [pretitleEl, titleEl]);
			cardEl.prepend(headerContentEl);
			if (introEl) {
				introEl.remove();
				return [introEl];
			}
			return [];
		}

		function applyFullHeightImageLayout(): HTMLElement[] {
			addAll(headerContentEl, [pretitleEl, titleEl, bylineEl]);
			cardEl.prepend(headerContentEl);
			bodyEl.remove();
			if (introEl) {
				introEl.remove();
				return [introEl];
			}
			return [];
		}

		function addAll(target: HTMLElement, nodes: Array<Element | null>) {
			nodes.forEach((node) => {
				if (node) {
					target.appendChild(node);
				}
			});
		}

		function detectAndHandleDynamicOverflow(checkImageSqueeze: boolean): HTMLElement[] {
			const imageWrapperEl = cardEl.querySelector('.image-wrapper') as HTMLElement;
			if (!imageWrapperEl) return [];

			const naturalHeight = imageWrapperEl.clientHeight;
			const cardOverflow = cardEl.scrollHeight - cardEl.offsetHeight;

			if (cardOverflow > 0 && naturalHeight > 0) {
				if (checkImageSqueeze) {
					const heightReduction = cardOverflow / naturalHeight;
					if (heightReduction <= 0.2) return [];
				}

				const overflow: HTMLElement[] = [];

				bodyEl.remove();

				if (introEl) {
					introEl.remove();
					overflow.unshift(introEl);
				}

				return overflow;
			}

			return [];
		}
	}

	renderCard(chunk: NodeChunk, index: number) {
		let pendingFigure: HTMLElement | null = chunk.figures[0];
		if (shouldBeRenderedInline(pendingFigure)) {
			chunk.figures.shift();
			// If first node is H2, insert inline figure after heading
			if (chunk.nodes[0]?.localName === 'h2') {
				chunk.nodes.splice(1, 0, pendingFigure);
			} else {
				chunk.nodes.unshift(pendingFigure);
			}

			// If there'a an additional image, render the first image the first col, and the second image as a large image
			if (chunk.figures.length) {
				chunk.layout = '1col-text-2col-image';
			}
			pendingFigure = chunk.figures[0];
		}

		let articleBody = div(chunk.nodes.map((el: HTMLElement) => el.outerHTML).join('\n'), 'article-body');
		if (chunk.extraNodes?.[0]?.length) {
			chunk.extraNodes.forEach((nodes, bodyPartIndex) => {
				if (!nodes.length) {
					return;
				}

				articleBody += div(
					nodes.map((el: HTMLElement) => el.outerHTML).join('\n'),
					`article-body article-body--part-${bodyPartIndex + 2}`
				);
			});
		}

		const markup = `
	${pendingFigure?.outerHTML || ''}
	${articleBody}
`.trim();
		this.addCard(this.createCard(index, chunk, markup, chunk.layout), null);

		function shouldBeRenderedInline(figure: HTMLElement) {
			if (!figure) {
				return false;
			}

			if (figure.dataset.render === 'inline') {
				return true;
			}

			return figure.classList.contains('picture--small') && figure.classList.contains('picture--square');
		}
	}

	addCard(cardEl: HTMLElement, cardComponent: CardComponent | null) {
		this.el.appendChild(cardEl);
		this.cardEls.push(cardEl);
		this.cardComponents.push(cardComponent);
		this.cardCount = this.cardEls.length;

		const progressIndicators = this.articleEl.querySelector('.article-progress');
		const progressIndicator = createElement(this.el.ownerDocument, 'div', 'article-progress__page');
		progressIndicator.dataset.action = 'activate-card';
		this.updateProgressIndicator(progressIndicator, this.cardCount);
		progressIndicators?.appendChild(progressIndicator);
	}

	removeLastCard() {
		const cardEl = this.cardEls.at(-1);
		if (!cardEl) {
			return;
		}
		this.removeCard(cardEl);
	}

	getNextCardWithArticleBody(cardEl: HTMLElement, layouts: ArticleCardLayoutRecord) {
		let nextCard = cardEl.nextElementSibling as HTMLElement;
		if (nextCard?.classList.contains('card--cloned-preview')) {
			nextCard = nextCard.nextElementSibling as HTMLElement;
		}

		if (!nextCard) {
			return;
		}

		let nextCardArticleBodyEl = nextCard.querySelector('.article-body');
		if (nextCardArticleBodyEl) {
			return nextCard;
		}

		while (!nextCardArticleBodyEl) {
			nextCard = nextCard.nextElementSibling as HTMLElement;
			if (!nextCard) {
				nextCard = this.createCard(this.cardCount, null, null, layouts[this.cardCount + 1]);
				this.addCard(nextCard, null);
			}
			nextCardArticleBodyEl = nextCard.querySelector('.article-body');
		}

		return nextCard;
	}

	createCard(
		index: number,
		chunk: NodeChunk | null,
		markup: string | null,
		layout: string | undefined,
		classNames: string[] = []
	) {
		classNames.unshift('card');

		if (this.item.isArticleGroup) {
			classNames.push('card--article-group');
		}

		const el = createElement(this.el.ownerDocument, 'div', classNames);
		el.innerHTML = markup ?? `<div class="article-body"></div>`;

		const cardNumber = index + 1;
		this.updateCardNumber(el, cardNumber);

		if (layout) {
			el.dataset.layout = layout;
		}
		if (chunk) {
			el.dataset.cardTextColumns = String(Math.ceil(chunk.chars / CHARS_PER_COLUMN));
			el.dataset.chars = String(chunk.chars);
			el.dataset.figureCount = String(chunk.figures?.length || 0);
		} else {
			el.dataset.cardTextColumns = '0';
			el.dataset.chars = '0';
			el.dataset.figureCount = '0';
		}

		return el;
	}

	private beforePostProcess() {
		// console.log('> post process');
		this.el.classList.add('cards--processing');
		this.disconnectIntersectionObserver();
	}

	private afterPostProcess() {
		// console.log('< post process done');
		this.el.classList.add('no-animations');
		this.el.classList.remove('cards--processing');
		this.hasPostProcessed = true;
		requestAnimationFrame(() => {
			this.el.classList.remove('no-animations');
			this.connectIntersectionObserver();
		});

		this.activateCard(this.targetCardNumberAfterPostProcess || this.currentCardNumber, false, 'postprocess');
		this.targetCardNumberAfterPostProcess = null;
	}

	postProcessCards(layouts: ArticleCardLayoutRecord, forceRedraw = false) {
		if (this.hasPostProcessed && !forceRedraw) {
			this.connectIntersectionObserver();
			return;
		}

		if (forceRedraw) {
			this.renderCards(layouts);
		}

		const articleBodyEl = this.el.querySelector('.article-body');
		if (!articleBodyEl) {
			return;
		}
		this.beforePostProcess();

		let index = 0;
		while (this.cardEls[index] && index < 40) {
			const cardEl = this.cardEls[index];
			this.replaceImageUrls(cardEl);
			// this.optimizeCards(cardEl, layouts);
			// TODO: This may not properly work with prechunked content
			cardEl.classList.add('card--overflow-checking');
			this.checkCardForOverflowingContent(cardEl, layouts);
			cardEl.classList.remove('card--overflow-checking');
			index++;
		}

		this.cleanUpEmptyCards();

		const styles = getComputedStyle(articleBodyEl);
		const baseline = Number.parseFloat(styles.lineHeight);
		this.cardEls.forEach((cardEl) => {
			adjustBaselineOffsets(cardEl, baseline);
		});
		this.afterPostProcess();
	}

	private cleanUpCards() {
		for (let i = this.cardEls.length - 1; i >= 0; i--) {
			const cardEl = this.cardEls[i];
			cardEl.querySelectorAll('p:empty').forEach((element) => {
				element.remove();
			});
			cardEl.querySelectorAll('style:only-child').forEach((element) => {
				element.remove();
			});
			cardEl.querySelectorAll('.article-body:empty').forEach((element) => {
				element.remove();
			});

			if (!cardEl.childElementCount) {
				this.removeCard(cardEl);
			}
		}
	}

	private cleanUpEmptyCards() {
		let lastCard = this.cardEls.at(-1);
		while (lastCard && isEmptyCard(lastCard)) {
			this.removeLastCard();
			lastCard = this.cardEls.at(-1);
		}

		function isEmptyCard(cardEl: HTMLElement) {
			const bodyEl = cardEl.querySelector<HTMLDivElement>('.article-body');
			const pictureWrapperEl = cardEl.querySelector<HTMLDivElement>('.picture');
			if (pictureWrapperEl) return false;
			if (!bodyEl || !bodyEl.firstElementChild) return true;
			// This is a special case for DN
			return bodyEl.innerHTML.trim() === '<p>*</p>';
		}
	}

	private getOrCreateNextCardBodyEl(cardEl: HTMLElement, layouts: ArticleCardLayoutRecord): HTMLElement | null {
		let nextCard = cardEl.nextElementSibling as HTMLElement;
		if (nextCard?.classList.contains('card--cloned-preview')) {
			nextCard = nextCard.nextElementSibling as HTMLElement;
		}

		if (nextCard?.querySelector('.article-body')?.firstElementChild?.classList?.contains('page-break-before')) {
			// TODO: Insert new card before the next card
		}

		if (!nextCard) {
			nextCard = this.createCard(this.cardCount, null, null, layouts[this.cardCount + 1]);
			this.addCard(nextCard, null);
		}

		return nextCard.querySelector<HTMLElement>('.article-body');
	}

	checkCardForOverflowingContent(cardEl: HTMLElement, layouts: ArticleCardLayoutRecord) {
		const bodyEl = cardEl.querySelector<HTMLDivElement>('.article-body');
		if (!bodyEl) {
			return;
		}

		bodyEl.style.setProperty('--offset-top', `${bodyEl.offsetTop}px`);

		// Push overflowing content down to the next card.
		this.pushDownOverflowingContent(cardEl, bodyEl, layouts);

		// After pushing content down, check for and move orphans.
		const lastElement = bodyEl.lastElementChild as HTMLElement;
		if (lastElement && shouldNotBeOrphan(lastElement) && lastElement !== bodyEl.firstElementChild) {
			const nextBodyEl = this.getOrCreateNextCardBodyEl(cardEl, layouts);
			nextBodyEl?.prepend(lastElement);
		}

		// Update card metadata after content changes
		this.updateCardMetadata(cardEl);
	}

	private updateCardMetadata(cardEl: HTMLElement) {
		const bodyEls = cardEl.querySelectorAll<HTMLDivElement>('.article-body');
		const figureEls = cardEl.querySelectorAll('.figure');

		let charCount = 0;
		bodyEls.forEach((bodyEl) => {
			charCount += bodyEl.textContent?.length || 0;
		});

		cardEl.dataset.chars = String(charCount);
		cardEl.dataset.figureCount = String(figureEls.length);
		cardEl.dataset.cardTextColumns = String(Math.ceil(charCount / CHARS_PER_COLUMN));
	}

	private pushDownOverflowingContent(cardEl: HTMLElement, bodyEl: HTMLElement, layouts: ArticleCardLayoutRecord) {
		const styles = getComputedStyle(bodyEl);
		const baseline = Number.parseFloat(styles.lineHeight);
		let sanityCounter = 10; // Max attempts to fix overflow for a single card

		while (
			(elementIsOverflowing(cardEl, this.mobileMode.matches) ||
				elementIsOverflowing(bodyEl, this.mobileMode.matches)) &&
			sanityCounter-- > 0
		) {
			if (bodyEl.childElementCount === 0) {
				break;
			}

			// If the card only has one child and is still overflowing, we can't fix it by moving content.
			if (bodyEl.childElementCount === 1 && !isFirstCard(cardEl)) {
				break;
			}

			const overflowPxCount = Math.max(
				cardEl.scrollHeight - cardEl.offsetHeight,
				bodyEl.scrollHeight - bodyEl.offsetHeight
			);

			const elementsToMove: HTMLElement[] = [];
			let movedHeight = 0;
			const children = Array.from(bodyEl.children).reverse() as HTMLElement[];

			for (const child of children) {
				elementsToMove.unshift(child);
				movedHeight += child.offsetHeight + baseline;

				// If we've collected enough elements to likely fix the overflow, stop collecting.
				if (movedHeight >= overflowPxCount && !shouldNotBeOrphan(child.previousElementSibling as HTMLElement)) {
					break;
				}
			}

			if (elementsToMove.length === 0) {
				break;
			}

			// If all elements are selected to be moved, and it's not the first card,
			// it implies the content is too large to begin with. Avoid moving.
			if (elementsToMove.length === bodyEl.childElementCount && !isFirstCard(cardEl)) {
				break;
			}

			const nextBodyEl = this.getOrCreateNextCardBodyEl(cardEl, layouts);
			if (!nextBodyEl) {
				break;
			}

			const nextCardEl = nextBodyEl.closest('.card') as HTMLElement;

			elementsToMove.forEach((el) => {
				el.remove();
			});
			const nextCardInlineFigureEl = nextBodyEl.querySelector('.figure');
			if (nextCardInlineFigureEl) {
				nextCardInlineFigureEl.after(...elementsToMove);
			} else {
				nextBodyEl.prepend(...elementsToMove);
			}

			// Update metadata for the next card after moving content into it
			if (nextCardEl) {
				this.updateCardMetadata(nextCardEl);
			}
		}
	}

	replaceImageUrls(cardEl: HTMLElement) {
		const pictureWrapperEl = cardEl.querySelector<HTMLDivElement>('.picture');
		if (!pictureWrapperEl) {
			return;
		}
		const imgEl = pictureWrapperEl.querySelector('img');
		if (imgEl) {
			const format = getFormatFromDimensions(imgEl.clientWidth, imgEl.clientHeight);
			if (format === 'square' && imgEl.dataset.srcSquare) {
				imgEl.src = imgEl.dataset.srcSquare;
				return;
			}
			if (format === 'wide' && imgEl.dataset.srcWide) {
				imgEl.src = imgEl.dataset.srcWide;
				return;
			}

			if (imgEl.complete) {
				onImageReady(imgEl);
			} else {
				imgEl.onload = () => onImageReady(imgEl);
			}
		}

		function onImageReady(imgEl: HTMLImageElement) {
			const renderedRatio = imgEl.clientWidth / imgEl.clientHeight;
			const intrinsicRatio = imgEl.naturalWidth / imgEl.naturalHeight;
			// If the deviation from desired and actual aspect ratio is large, use contain by default
			// TODO: Request new image with crop and area of interest applied
			if (Number.isFinite(renderedRatio) && Math.abs(renderedRatio - intrinsicRatio) > 0.5) {
				// TODO: Disabled for demo
				pictureWrapperEl?.style.setProperty(
					'--inline-figure-aspect-ratio',
					`${imgEl.naturalWidth} / ${imgEl.naturalHeight}`
				);
			}
		}
	}

	optimizeCards(cardEl: HTMLElement, layouts: ArticleCardLayoutRecord) {
		const currentCardArticleBodyEl = cardEl.querySelector<HTMLDivElement>('.article-body');
		if (!currentCardArticleBodyEl) {
			return;
		}

		currentCardArticleBodyEl.style.setProperty('--offset-top', `${currentCardArticleBodyEl.offsetTop}px`);
		const figureEl = cardEl.querySelector(':root > .figure') as HTMLElement;
		const amediaEmbeds = Array.from(cardEl.querySelectorAll('amedia-embed')) as HTMLElement[];

		if (cardEl.dataset.cardNumber === '1' || amediaEmbeds.length > 1) {
			this.checkCardForOverflowingContent(cardEl, layouts);
			return;
		}

		if (!figureEl) {
			return;
		}

		cardEl.dataset.layout = '2col-image-top-right';
		const articleBodyPartTwo = createElement(cardEl.ownerDocument, 'div', [
			'article-body',
			'article-body--part-two'
		]);
		const contentHeight = cardEl.offsetHeight - 30;
		currentCardArticleBodyEl.style.setProperty('--offset-top', `${currentCardArticleBodyEl.offsetTop}px`);
		const nodes = Array.from(currentCardArticleBodyEl.children) as HTMLElement[];
		const styles = getComputedStyle(currentCardArticleBodyEl);
		const baseline = Number.parseFloat(styles.lineHeight);

		const {partTwo} = nodes.reduce(
			(acc, curr: HTMLElement) => {
				if (acc.partOne.height + curr.offsetHeight + baseline <= contentHeight) {
					acc.partOne.nodes.push(curr);
					acc.partOne.height += curr.offsetHeight + baseline;
				} else {
					acc.partTwo.height += curr.offsetHeight + baseline;
					acc.partTwo.nodes.push(curr);
				}
				return acc;
			},
			{partOne: {height: 0, nodes: [] as HTMLElement[]}, partTwo: {height: 0, nodes: [] as HTMLElement[]}}
		);

		cardEl.style.setProperty('--figure-height', `${figureEl.offsetHeight}px`);
		articleBodyPartTwo.append(...partTwo.nodes);
		cardEl.append(articleBodyPartTwo);
	}

	removeActiveDialog() {
		if (this.dialogEl) {
			this.dialogEl.close();
			this.dialogEl.remove();
		}
	}

	getCardChunk(cardNumber: number) {
		return this.chunks[cardNumber - 1];
	}

	getCurrentCardEl() {
		return this.currentCardEl;
	}

	getCardElByNumber(number: number) {
		return this.cardEls[number - 1];
	}

	/**
	 * Updates card state attributes based on the card's position relative to the current card number.
	 * State is automatically derived from the card's number. Reads cardNumber from dataset.
	 */
	private updateCardState(cardEl: HTMLElement) {
		const cardNumber = Number(cardEl.dataset.cardNumber);
		const offset = cardNumber - this.currentCardNumber;
		cardEl.dataset.cardState = offset === 0 ? 'current' : offset < 0 ? 'past' : 'future';
		cardEl.dataset.cardOffset = String(offset);
		cardEl.style.setProperty('--card-offset', String(offset));
	}

	/**
	 * Updates a card's number and related attributes. Call this when renumbering cards.
	 */
	private updateCardNumber(cardEl: HTMLElement, newNumber: number) {
		cardEl.dataset.cardNumber = String(newNumber);
		cardEl.style.setProperty('--card-number', String(newNumber));
		this.updateCardState(cardEl);
	}

	/**
	 * Updates a progress indicator's attributes and state based on card number
	 */
	private updateProgressIndicator(indicator: HTMLElement, cardNumber: number) {
		const state =
			cardNumber === this.currentCardNumber ? 'current' : cardNumber < this.currentCardNumber ? 'past' : 'future';

		indicator.dataset.progressIndex = String(cardNumber);
		indicator.dataset.actionValue = String(cardNumber);
		indicator.dataset.cardState = state;
	}

	/**
	 * Renumbers all cards starting from a given index to ensure consistent numbering
	 */
	private renumberCardsFrom(startIndex: number) {
		for (let i = startIndex; i < this.cardEls.length; i++) {
			const cardEl = this.cardEls[i];
			const newNumber = i + 1;
			this.updateCardNumber(cardEl, newNumber);
		}
	}

	/**
	 * Updates all progress indicators to reflect the current card state
	 */
	private updateAllProgressIndicators() {
		const progressIndicators = this.articleEl.querySelectorAll('.article-progress__page');
		progressIndicators.forEach((indicator: Element) => {
			const cardNumber = Number((indicator as HTMLElement).dataset.progressIndex);
			this.updateProgressIndicator(indicator as HTMLElement, cardNumber);
		});
	}

	/**
	 * Renumbers all progress indicators after a card has been removed.
	 * All indicators after the removed card number need to be decremented.
	 */
	private renumberProgressIndicatorsAfterRemoval(removedCardNumber: number) {
		const progressIndicators = Array.from(this.articleEl.querySelectorAll<HTMLElement>('.article-progress__page'));

		// Decrement all indicators that come after the removed card
		progressIndicators.forEach((indicator) => {
			const indicatorNumber = Number(indicator.dataset.progressIndex);
			if (indicatorNumber > removedCardNumber) {
				this.updateProgressIndicator(indicator, indicatorNumber - 1);
			}
		});
	}

	private removeCard(cardEl: HTMLElement) {
		const cardNumber = Number(cardEl.dataset.cardNumber);
		const index = this.cardEls.indexOf(cardEl);

		if (index === -1) {
			return;
		}

		// Remove from arrays and DOM
		this.cardEls.splice(index, 1);
		this.cardComponents.splice(index, 1);
		this.unobserveCard(cardEl);
		cardEl.remove();

		// Remove corresponding progress indicator
		const progressIndicator = this.articleEl.querySelector(
			`.article-progress__page[data-progress-index="${cardNumber}"]`
		);
		progressIndicator?.remove();

		// Renumber remaining cards and progress indicators
		this.renumberCardsFrom(index);
		this.renumberProgressIndicatorsAfterRemoval(cardNumber);

		this.cardCount = this.cardEls.length;
	}

	isOnLastCard() {
		return this.cardCount === this.currentCardNumber;
	}
}

function isFirstCard(cardEl: HTMLElement) {
	return cardEl.dataset.cardNumber === '1';
}

function elementIsOverflowing(el: HTMLElement, mobileMode: boolean) {
	if (!el) {
		return false;
	}

	// Check for vertical overflow, and horizontal overflow on desktop.
	return el.scrollHeight > el.offsetHeight + 1 || (!mobileMode && el.scrollWidth > el.offsetWidth + 1);
}

function adjustBaselineOffsets(cardEl: HTMLElement, baseline: number) {
	cardEl.querySelectorAll<HTMLElement>('h3, .headline').forEach((headlineEl) => {
		const baselineDiff = getBaselineDiff(baseline, headlineEl.offsetHeight);
		if (baselineDiff !== baseline) {
			headlineEl.style.marginBottom = `${baselineDiff}px`;
		}
	});

	cardEl.querySelectorAll<HTMLElement>('.article-body [data-component="picture"]').forEach((pictureEl) => {
		const captionEl = getCaptionSibling(pictureEl) as HTMLElement;
		if (captionEl) {
			captionEl.style.marginBottom = `${0}px`;
			const nextEl = captionEl.nextElementSibling as HTMLElement;
			if (nextEl) {
				const combinedHeight = pictureEl.offsetHeight + captionEl.offsetHeight;
				nextEl.style.marginTop = `${getBaselineDiff(baseline, combinedHeight)}px`;
			}
		} else {
			pictureEl.style.marginBottom = `${getBaselineDiff(baseline, pictureEl.offsetHeight)}px`;
		}
	});
}

function getCaptionSibling(el: HTMLElement) {
	if (!el.nextElementSibling?.classList.contains('caption')) {
		return null;
	}

	return el.nextElementSibling;
}

function getBaselineDiff(baseline: number, height: number) {
	if (height === 0) {
		return 0;
	}

	const extraHeight = height % baseline;
	if (extraHeight === 0) {
		return 0;
	}
	return baseline - extraHeight;
}

function smoothScrollTo(
	element: HTMLElement,
	containerEl: HTMLElement,
	duration = 150,
	easing = 'ease-in-out',
	axis: 'horizontal' | 'vertical' = 'horizontal'
) {
	const startPosition = axis === 'horizontal' ? containerEl.scrollLeft : containerEl.scrollTop;

	const targetPosition = axis === 'horizontal' ? element.offsetLeft : element.offsetTop;

	const distance = targetPosition - startPosition;
	if (distance === 0) {
		return distance;
	}

	// Disable scroll snap temporarily, is restored below
	if (containerEl instanceof HTMLElement) {
		containerEl.style.scrollSnapType = 'none';
	}

	let startTime: number | null = null;

	const easingFunctions: Record<string, (t: number) => number> = {
		linear: (t: number) => t,
		'ease-in-out': (t: number) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
		'ease-in': (t: number) => t * t,
		'ease-out': (t: number) => t * (2 - t)
	};

	const easeFn = easingFunctions[easing] || easingFunctions['ease-in-out'];
	requestAnimationFrame(animation);

	return distance;

	function animation(currentTime: number) {
		if (startTime === null) startTime = currentTime;
		const timeElapsed = currentTime - startTime;
		const progress = Math.min(timeElapsed / duration, 1);
		const easedProgress = easeFn(progress);

		const newPosition = startPosition + distance * easedProgress;

		if (axis === 'horizontal') {
			containerEl.scrollLeft = newPosition;
		} else {
			containerEl.scrollTop = newPosition;
		}

		if (progress < 1) {
			requestAnimationFrame(animation);
		} else {
			// Re-enable scroll snap after animation completes
			// Small delay to ensure the scroll has settled
			setTimeout(() => {
				containerEl.style.scrollSnapType = getDefaultScrollSnap();
			}, 50);
		}
	}

	function getDefaultScrollSnap() {
		return axis === 'horizontal' ? 'x mandatory' : 'y mandatory';
	}
}

function getExpandedBoundingRect(el: HTMLElement) {
	const rect = el.getBoundingClientRect();
	const styles = getComputedStyle(el);
	const shapeMargin = Number.parseFloat(styles.shapeMargin) || 0;

	return {
		top: rect.top - shapeMargin,
		right: rect.right + shapeMargin,
		bottom: rect.bottom + shapeMargin,
		left: rect.left - shapeMargin,
		width: rect.width + shapeMargin * 2,
		height: rect.height + shapeMargin * 2,
		x: rect.x - shapeMargin,
		y: rect.y - shapeMargin
	};
}
